import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription, forkJoin, Observable, Subject, of } from 'rxjs';

import { SapiService } from '../../../services/sapi.service';
import { Publication, CustomField } from '../../../interfaces/publication';
import { TranslateService } from '@ngx-translate/core';
import { NgForm } from '@angular/forms';
import { OverlayService, DialogDefinition } from '../../../services/overlay.service';
import { RouterStateSnapshot } from '@angular/router';
import { CustomFieldsService } from '../../../services/custom-fields.service';
import { catchError, map } from 'rxjs/operators';
import { bbDeepCopy } from '../../../shared/utils/bbDeepCopy.util';
import { PublicationService } from 'app/services/publication.service';

@Component({
	templateUrl: './publication-settings.component.html',
	styleUrls: ['./publication-settings.component.scss']
})

export class PublicationSettingsComponent implements OnInit, OnDestroy {
	@ViewChild('formRef', { static: true }) form: NgForm;
	subscriptions = new Subscription();
	publication: any = {};
	previousPublication: Publication;
	extraPlayoutFieldsOptions: any[] = [];
	ipFilters: any[] = [];
	fieldsToSave: any = {};
	deepLinkFields = '';
	publicationFields: CustomField[] = [];
	newPublicationFields: CustomField[] = [];
	newFieldName = '';
	reservedFieldNames: string[] = ('id status type name #text cdn extraPlayoutFields disablePositions deeplinkTemplate autoDeeplinkDomains ipStatsFilter' +
		' publisherid whitelabelHeaderImageId whitelabelLogoId whitelabelFaviconId whitelabelTitle reactTitle reactLogoId reactHighlight' +
		' label baseurl baseuri sourcepath hascmsystems statsserver statspublication player playoutconfig customfields prebidParams ' +
		' ovpCssOverride ovpCustomHomeUrl customCode customThumbnailSets').split(' ');
	whitelabelLogoId: string;
	whitelabelHeaderImageId: string;
	whitelabelFaviconId: string;
	reactLogoId: string;

	constructor(
		private sapiService: SapiService,
		private overlayService: OverlayService,
		private translate: TranslateService,
		private customFieldsService: CustomFieldsService,
		private publicationService: PublicationService
	) { }

	ngOnInit() {
		this.populateFields();
		this.fetchPublicationData();
		this.generateDeepLinkFields();

		this.subscriptions.add(this.form.form.valueChanges.subscribe((formChanges) => {
			for (const key in formChanges) {
				if (typeof formChanges[key] !== 'undefined') {
					if (formChanges[key] !== this.fieldsToSave[key] && typeof this.form.controls[key] !== 'undefined' && !this.form.controls[key].pristine) {
						this.fieldsToSave[key] = formChanges[key];
					}
				}
			}
		}));
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	generateDeepLinkFields() {

		const customFields = this.customFieldsService.getCustomFields('mediaclip');

		const props = ['id', 'title'];

		// add all string/int fields
		if (customFields && customFields.length) {
			customFields.forEach((field) => {
				if (field.itype === 'int' || field.itype === 'string' || field.type === 'boolean' || field.type === 'singleSelect') {
					props.push(field.name);
				}
			});
		}

		// generate html
		let deepLinkFields = '<ul>';

		props.forEach(prop => {
			deepLinkFields += '<li>{{' + prop + '}}</li>';
		});

		deepLinkFields += '</ul>';

		this.deepLinkFields = deepLinkFields;
	}

	allowNavigateAway(currentState: RouterStateSnapshot, nextState: RouterStateSnapshot): Observable<boolean> | boolean {
		// allow if not dirty
		if (this.form.form && !this.form.form.dirty) {
			return true;
		}

		// allow if navigating to sibling route
		const currentParent = currentState.url.split('/').slice(0, -1).join('/');
		const nextParent = nextState.url.split('/').slice(0, -1).join('/');

		if (currentParent === nextParent) {
			return true;
		}

		// ask user
		const allowNavigationObservable = new Subject<boolean>();

		const dialog = {
			icon: 'warning',
			title: this.translate.instant('PROMPT_SURE_TO_LEAVE_APPLICATION'),
			contents: this.translate.instant('PROMPT_SURE_TO_LEAVE_PAGE'),
			showSave: true,
			onConfirm: () => {
				allowNavigationObservable.next(true);
			},
			onCancel: () => {
				allowNavigationObservable.next(false);
			},
			onSaveAndClose: () => {
				this.save();
				allowNavigationObservable.next(true);
			}
		};

		this.overlayService.createDialog(dialog);

		return allowNavigationObservable;
	}

	fetchPublicationData() {
		this.overlayService.setLoading(true);
		const fields = 'ipStatsFilter deeplinkTemplate disablePositions extraPlayoutFields customCode'.split(' ');
		forkJoin(fields.map((fieldName) => this.sapiService.getUrl(`/publication/${fieldName}`).pipe(
			// keep the forkJoin observable alive
			catchError((err) => {
				let ret;
				switch (fieldName) {
					default:
						ret = '';
						break;

					case 'disablePositions':
						ret = 'false';
						break;

					case 'extraPlayoutFields':
						ret = [];
						break;
				}
				return of(ret);
			}),
			map((val) => {
				// map publication fields to text since the publication object normally returns them as strings
				if (typeof val === 'boolean') {
					if (val) {
						return 'true';
					} else {
						return 'false';
					}
				} else {
					return val;
				}
			})
		))).subscribe((data) => {
			fields.forEach((fieldName, i) => {
				this.publication[fieldName] = data[i];
			});
			if (typeof this.publication.ipStatsFilter === 'string' && this.publication.ipStatsFilter) {
				this.ipFilters = this.publication.ipStatsFilter.split(',');
			} else {
				this.ipFilters = [];
			}
			this.overlayService.setLoading(false);
		});

		this.sapiService.getUrl('/publication').subscribe((pub: Publication) => {
			if (pub) {
				this.publication = bbDeepCopy(pub);
				this.fillPublicationFields(this.publication);
				this.whitelabelLogoId = pub.whitelabelLogoId;
				this.whitelabelHeaderImageId = pub.whitelabelHeaderImageId;
				this.whitelabelFaviconId = pub.whitelabelFaviconId;
				this.reactLogoId = pub.reactLogoId;
				this.overlayService.setLoading(false);
			}
		});
	}

	addNewFieldClick() {
		if (this.newFieldName) {
			if (Object.keys(this.publication).indexOf(this.newFieldName) !== -1 || this.reservedFieldNames.indexOf(this.newFieldName) !== -1) {
				this.overlayService.createNotice({
					icon: 'error',
					title: this.translate.instant('Could not create field'),
					contents: this.translate.instant('This field name is reserved or already exists'),
				});
			} else {
				const newField: CustomField = {
					name: this.newFieldName,
					label: this.newFieldName,
					itype: 'string',
					type: 'string'
				};
				this.publication[this.newFieldName] = '';
				this.publicationFields.unshift(newField);
				this.newFieldName = '';
			}
		}
	}

	fillPublicationFields(pub: Publication) {
		this.publicationFields = [];
		Object.keys(pub)
			.filter((key) => this.reservedFieldNames.indexOf(key) === -1)
			.forEach((key) => {

				const field: CustomField = {
					name: key,
					label: key,
					itype: 'string',
					type: 'string'
				};
				const val = pub[key];

				switch (typeof val) {
					case 'string':
						if (val === 'true' || val === 'false') {
							field.itype = 'checkbox';
						} else if (val.length > 40) {
							field.itype = 'text';
						}
						this.publicationFields.push(field);

						break;

					case 'boolean':
						field.type = 'boolean';
						field.itype = 'checkbox';
						this.publicationFields.push(field);
						break;

					case 'object':
						if (val.length) {
							field.itype = 'list';
							field.type = 'multistring';
							field.extendable = true;
							if (val[0] && typeof val[0] === 'string') {
								this.publicationFields.push(field);
							}
						} else {
							field.itype = 'objectEditor';
							field.type = 'object';
							this.publicationFields.push(field);
						}
						break;
				}
			});
	}

	deleteCustomField(fieldName: string) {
		if (!fieldName) {
			return;
		}
		const dialog: DialogDefinition = {
			icon: 'warning',
			title: `Removing field "${fieldName}"`,
			contents: 'Are you sure?',
			onConfirm: () => {
				this.sapiService.deleteField(`publication/${fieldName}`).subscribe(() => {
					this.overlayService.createToast(`The field "${fieldName}" was succesfully deleted`);
					this.publicationFields.splice(this.publicationFields.findIndex((pubField) => pubField.name === fieldName), 1);
					delete this.publication[fieldName];
				});
			}
		};

		this.overlayService.createDialog(dialog);
	}

	onFiltersChanged(value: string[]) {
		this.fieldsToSave.ipStatsFilter = value ? value.join(',') : '';
		this.form.form.markAsDirty();
	}

	cancel() {
		this.fieldsToSave = {};
		this.form.form.reset();
		this.form.form.markAsPristine();
		this.fetchPublicationData();
	}

	save() {
		if (this.form) {
			this.form.onSubmit({} as Event);
			if (this.form.form.status === 'INVALID') {
				return;
			}
		}

		if (!this.fieldsToSave || Object.keys(this.fieldsToSave).length === 0) {
			this.overlayService.createToast(this.translate.instant('NOTHING_TO_SAVE'), 'times');
			return;
		}
		this.overlayService.setLoading(true);

		// backend will remove fields with value '' or null
		this.sapiService.putUrl('/publication/config', this.fieldsToSave).subscribe(() => {
			this.overlayService.createToast(this.translate.instant('ENTITY_SAVED', {
				entity: this.translate.instant('PUBLICATION_SETTINGS'),
				id: ''
			}), 'save');
			// since the backend doesn't return the created/altered object + fetchPublicationData transforms data
			this.cancel();
			this.publicationService.updateConfig();
		}, (e) => {
			if (e && e.error && e.error.body) {
				this.overlayService.createToast(e.error.body, 'times');
			} else {
				this.overlayService.createToast(this.translate.instant('UNKNOWN_ERROR'), 'times');
			}
			this.overlayService.setLoading(false);
		});
	}

	setSelectedImage(varName: string) {
		if (this.publication[varName] !== this[varName]) {
			this.fieldsToSave[varName] = this[varName];
			this.form.form.markAsDirty();
		}
	}

	populateFields() {
		this.extraPlayoutFieldsOptions = [
			{ label: this.translate.instant('AUTO_HEIGHT'), value: 'autoHeight' },
			{ label: this.translate.instant('USE_NEW_SKIN'), value: 'skinPreview' },
			{ label: this.translate.instant('ISOLATE_PLAYER_STYLING'), value: 'isolatePlayer' },
			{ label: this.translate.instant('FORCE_CAN_AUTOPLAY'), value: 'forceCanAutoPlay' },
			{ label: this.translate.instant('ADUNIT_AUTOPLAY'), value: 'adunitAutoPlay' },
			{ label: this.translate.instant('PLAYOUT_CLEAR_BOTH_OPTION'), value: 'clearBothOption' },
			{ label: this.translate.instant('AUTHOR_AND_COPYRIGHT_ALIGNMENT'), value: 'authorCopyrightAlign' },
			{ label: this.translate.instant('NO_SOUND_INFLUENCE_NO_AUTOPLAY'), value: 'nsiNoAutoPlay' },
			{ label: this.translate.instant('NO_SOUND_INFLUENCE_NO_PLAYER'), value: 'nsiNoPlayer' },
			{ label: this.translate.instant('CUSTOM_CODE'), value: 'customCode' },
			{ label: this.translate.instant('SKIN_BEHAVIOUR'), value: 'skinBehaviour' },
			{ label: this.translate.instant('CUSTOM_SKIN_PATH'), value: 'customSkinPath' },
			{ label: this.translate.instant('NATIVE_CONTROLS'), value: 'nativeControls' },
			{ label: this.translate.instant('YOUTUBE_HOSTING'), value: 'youTubeHosting' },
			{ label: this.translate.instant('YOUTUBE_SKIN_IN_MAIN_PHASE'), value: 'youTubeSkinInMainPhase' },
			{ label: this.translate.instant('FORCE_NATIVE_FULLSCREEN'), value: 'forceNativeFullscreen' },
			{ label: this.translate.instant('AVOID_NATIVE_FULLSCREEN'), value: 'avoidNativeFullscreen' },
			{ label: this.translate.instant('KEEP_FRAME_VISIBLE_WHILE_LOADING'), value: 'showFrameWhileLoading' },
			{ label: this.translate.instant('MUTE_WHILE_LOADING_SAFARI'), value: 'safariMuteWhileLoading' },
			{ label: this.translate.instant('DISABLE_STATISTICS_LOGGING'), value: 'noStats' },
			{ label: this.translate.instant('PIWIK_URL'), value: 'piwikUrl' },
			{ label: this.translate.instant('PWIKI_SITE_ID'), value: 'piwikSiteId' },
			{ label: this.translate.instant('GOOGLE_ANALYTICS_ID'), value: 'googleAnalyticsId' },
			{ label: this.translate.instant('CONTEXT_MENU_NAVIGATE'), value: 'disableContextMenuNavigate' },
			{ label: this.translate.instant('PLAYER_SIGNATURE'), value: 'playerSignature' },
			{ label: this.translate.instant('PLAYER_SIGNATURE_URL'), value: 'playerSignatureLink' },
			{ label: this.translate.instant('PLAYOUT_MINIMUM_CLIP_DURATION_FOR_PREROLL'), value: 'minClipDurationPreroll' },
			// {label: this.translate.instant('SKIN_ON_TIMELINE'), value: 'skinOnTimeline'},
			{ label: this.translate.instant('PLAYOUT_PREFERRED_PLAYMODE'), value: 'preferredPlayMode' },
			{ label: this.translate.instant('PLAYOUT_TEXT_ABOVE_PLAYER'), value: 'textAbovePlayer' },
			{ label: this.translate.instant('PLAYOUT_TEXT_CLOSE_PLAYER'), value: 'textCommercialSkip' },
			{ label: this.translate.instant('START_COLLAPSED'), value: 'startCollapsed' },
			{ label: this.translate.instant('FORCE_INVIEW_ACTIONS'), value: 'forceInview' },
			{ label: this.translate.instant('PLAY_IN_OVERLAY'), value: 'playInOverlay' },
			{ label: this.translate.instant('PLAYOUT_ALLOW_CUSTOM_CLICKLAYER'), value: 'allowCustomClickLayer' },
			{ label: this.translate.instant('PLAYOUT_EVENT_HANDLERS'), value: 'eventHanders' },
			{ label: this.translate.instant('VMAP'), value: 'vmap' },
			{ label: this.translate.instant('ALLOW_VIEWS_SORT'), value: 'allowViewsSort' },
			{ label: this.translate.instant('SHOW_EXTRA_PUBLICATION_OPTIONS'), value: 'showExtraPlayoutFields' },
			{ label: this.translate.instant('FORCE_ANDROID_NATIVE_VIDEO'), value: 'forceAndroidNativeVideo' },
			{ label: this.translate.instant('FORCE_IOS_NATIVE_VIDEO'), value: 'forceIOSNativeVideo' },
			{ label: this.translate.instant('USE_CORS'), value: 'useCors' },
			{ label: this.translate.instant('PROJECT_FLOWCHART'), value: 'projectFlowChart' },
			{ label: this.translate.instant('PROJECT_KPI_MANAGEMENT'), value: 'projectKpiManagement' },
			{ label: this.translate.instant('ENABLE_FACEBOOK'), value: 'enableFacebook' },
			{ label: this.translate.instant('ENABLE_INSTAGRAM'), value: 'enableInstagram' },
			{ label: this.translate.instant('BLOCK_INSECURE_VPAID'), value: 'blockInsecureVPAID' },
			{ label: this.translate.instant('PLAYOUT_IFRAME_BREAKOUT'), value: 'iframeBreakout' },
			{ label: this.translate.instant('MANAGE_SUBUSETYPE'), value: 'manageSubusetype' },
			{ label: this.translate.instant('USE_SUGGESTS'), value: 'suggest' },
			{ label: this.translate.instant('CONTENT_TYPE'), value: 'contenttype' },
			{ label: this.translate.instant('CUSTOM_FIELD_INCLUDED_IN_STATS'), value: 'includeInStats' },
			{ label: this.translate.instant('CHANNELS'), value: 'channels' },
			{ label: this.translate.instant('LINEUPS'), value: 'lineups' },
			{ label: this.translate.instant('DATAFEEDS'), value: 'datafeeds' },
			{ label: this.translate.instant('PLAYOUT_INTERACTIVE_LAYER_TIMELINE'), value: 'playoutTimelineId' },
			{ label: this.translate.instant('PLAYOUT_INTERACTIVE_TEMPLATE'), value: 'playoutTemplate' },
			{ label: this.translate.instant('ALLOW_OEMBED_EMBED'), value: 'allowOembed' },
			{ label: this.translate.instant('USE_SIMPLE_ROLES'), value: 'useSimpleRoles' },
			{ label: this.translate.instant('USE_LEGACY_ROLES'), value: 'useLegacyRoles' },
			{ label: this.translate.instant('USE_TRANSCRIPTION_SERVICE'), value: 'useTranscriptionService' },
			{ label: this.translate.instant('USE_WHITELABEL'), value: 'useWhitelabel' },
			{ label: this.translate.instant('BIDDER_ALIASING'), value: 'bidderAliasing' },
			{ label: this.translate.instant('SHOW_IMAGE_THUMBNAIL_TAB'), value: 'showImageThumbnailTab' }
		];
	}
}
